import React from 'react';
import { Link } from 'react-router-dom';
import { slugifyTag } from '../utils/tags';
import './InternalLinks.css';

/**
 * Internal linking component for SEO improvement
 * Helps distribute page authority and improve crawling
 */

const InternalLinks = ({ type = 'blog', currentSlug = '', currentTags = [], tags = [], title = 'Related Articles', maxLinks = 3 }) => {
  // Use currentTags if provided, fallback to tags
  const effectiveTags = currentTags || tags || [];
  
  // Blog-related internal links - updated with new blog posts
  const blogLinks = [
    {
      to: '/blog/context-engineering-for-llm-agents',
      title: 'Context Engineering: The Discipline That Replaced Prompt Engineering',
      description: 'Context budgets, compaction, tool-result hygiene, and just-in-time retrieval'
    },
    {
      to: '/blog/agent-memory-architectures-production',
      title: 'Agent Memory That Works: Three Tiers on Postgres',
      description: 'Write policies, supersession, consolidation jobs, and memory evals on pgvector'
    },
    {
      to: '/blog/multi-agent-systems-when-to-split',
      title: 'Multi-Agent Systems: When to Split One Agent into Many',
      description: 'The three real reasons to go multi-agent, and the cost math nobody shows'
    },
    {
      to: '/blog/production-ai-agents-architecture-patterns',
      title: 'Production AI Agents: Architecture Patterns That Survive Real Users',
      description: 'Bounded loops, checkpointed state, blast-radius tool tiers, and graceful degradation'
    },
    {
      to: '/blog/designing-tools-for-llm-agents',
      title: 'Designing Tools for LLM Agents',
      description: 'Tool naming, schemas, error messages as prompts, and where MCP fits'
    },
    {
      to: '/blog/evaluating-llm-agents-in-production',
      title: 'Evaluating AI Agents: Evals That Predict Production Behavior',
      description: 'A practical testing pyramid for non-deterministic agent systems'
    },
    {
      to: '/blog/agentic-rag-beyond-retrieve-then-read',
      title: 'Agentic RAG: Beyond Retrieve-Then-Read',
      description: 'When single-shot RAG plateaus and how agentic retrieval fixes it'
    },
    {
      to: '/blog/python-llm-agent-frameworks-production',
      title: 'Building Production-Ready AI Agents with Python',
      description: 'Beyond simple ChatGPT integrations - strategic architecture for reliable AI agents'
    },
    {
      to: '/blog/fastapi-websockets-realtime-applications',
      title: 'FastAPI + WebSockets: Building Real-Time Applications',
      description: 'Production-tested patterns for real-time applications that scale under load'
    },
    {
      to: '/blog/python-edge-computing-ml-iot-deployment',
      title: 'Python Edge Computing: ML Models to IoT Devices',
      description: 'Proven methodology for deploying Python ML workloads on edge hardware'
    },
    {
      to: '/blog/production-fastapi-blueprint',
      title: 'Production FastAPI Blueprint',
      description: 'Complete guide to production-ready FastAPI applications'
    },
    {
      to: '/blog/ai-integration-rag-in-production',
      title: 'AI Integration: RAG in Production',
      description: 'How to implement Retrieval-Augmented Generation systems'
    },
    {
      to: '/blog/python-performance-tips',
      title: 'Python Performance Tips',
      description: 'Techniques that actually improve Python performance'
    },
    {
      to: '/blog/microservices-fastapi-best-practices',
      title: 'Microservices with FastAPI',
      description: 'Best practices for FastAPI microservices architecture'
    }
  ];

  // Service-related internal links
  const serviceLinks = [
    {
      to: '/#services',
      title: 'Full-stack AI Engineering Services',
      description: 'AI agents and RAG, full-stack AI products, LLM fine-tuning, Python backends, cloud, and DevOps.'
    },
    {
      to: '/about',
      title: 'About PySolutions',
      description: 'Senior-led full-stack engineering studio shipping production AI products end to end.'
    },
    {
      to: '/contact',
      title: 'Start Your Engagement',
      description: 'Free consultation. Senior-led, weekly demos, replies within 24 hours on weekdays.'
    }
  ];

  // Tag-based links
  const getTagLinks = (tags) => {
    return tags.slice(0, 3).map(tag => ({
      to: `/blog/tags/${slugifyTag(tag)}`,
      title: `${tag} Articles`,
      description: `More articles about ${tag}`
    }));
  };

  const renderLinks = (links, title) => (
    <div className="internal-links-section">
      <h3 className="internal-links-title">{title}</h3>
      <div className="internal-links-grid">
        {links.map((link, index) => (
          <Link
            key={index}
            to={link.to}
            className="internal-link-card"
            aria-label={link.description}
          >
            <h4>{link.title}</h4>
            <p>{link.description}</p>
            <span className="link-arrow">→</span>
          </Link>
        ))}
      </div>
    </div>
  );

  if (type === 'blog') {
    const filteredBlogLinks = blogLinks.filter(link => 
      !link.to.includes(currentSlug)
    ).slice(0, maxLinks);
    
    return (
      <div className="internal-links">
        {renderLinks(filteredBlogLinks, title)}
        {effectiveTags.length > 0 && renderLinks(getTagLinks(effectiveTags), 'Explore by Topic')}
        {renderLinks(serviceLinks.slice(0, 2), 'Our Services')}
      </div>
    );
  }

  if (type === 'service') {
    return (
      <div className="internal-links">
        {renderLinks(blogLinks.slice(0, 3), 'Technical Insights')}
        {renderLinks(serviceLinks, 'Related Services')}
      </div>
    );
  }

  return null;
};

export default InternalLinks;
